import Phaser from 'phaser';
import { chooseAction } from '../game/ai/index.js';
import type { GameEvent, GameState } from '../game/types.js';
import type { GameStore } from '../store/GameStore.js';

// Visible "thinking" pause for actions with no animation (deploy,
// advance, fortify). Long enough to feel deliberate; short enough that
// a full AI turn doesn't drag.
const TICK_DELAY_MS = 600;

// After an attack the dice tray plays a ~1.4s animation. We wait past
// it before the AI's next tick so the player has time to read the
// outcome and the conquering tile's units settle on screen before any
// follow-up move.
const TICK_DELAY_AFTER_COMBAT_MS = 1500;

/**
 * Drives AI players by subscribing to GameStore and dispatching one
 * action per tick whenever the current player has `kind: 'ai'`.
 *
 * Loop mechanics: every successful dispatch fires the store listener
 * synchronously; the listener schedules the next tick if the same (or
 * another) AI is still up. So a multi-action AI turn unfolds as a chain
 * of timer callbacks, not a busy loop. When control passes to a human,
 * the listener simply doesn't schedule anything and the chain dies.
 */
export class AiDriver {
  private readonly scene: Phaser.Scene;
  private readonly store: GameStore;
  private unsubscribe: (() => void) | null = null;
  private pendingTick: Phaser.Time.TimerEvent | undefined;

  constructor(scene: Phaser.Scene, store: GameStore) {
    this.scene = scene;
    this.store = store;
  }

  start(): void {
    if (this.unsubscribe) return;
    this.unsubscribe = this.store.subscribe((state, events) => {
      this.handleStateChange(state, events);
    });
    // Kick the loop manually in case the game opens on an AI seat — the
    // store doesn't replay an initial event to new subscribers.
    this.handleStateChange(this.store.getState(), []);
  }

  stop(): void {
    if (this.pendingTick) {
      this.pendingTick.remove(false);
      this.pendingTick = undefined;
    }
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  private handleStateChange(
    state: GameState,
    events: readonly GameEvent[]
  ): void {
    // Cancel any tick that was scheduled by an earlier state — the new
    // state may have moved off the AI's turn (e.g. eliminated, victory).
    if (this.pendingTick) {
      this.pendingTick.remove(false);
      this.pendingTick = undefined;
    }
    if (state.status.kind !== 'inProgress') return;

    const current = state.players.find(
      (p) => p.id === state.turn.currentPlayer
    );
    if (!current || current.kind !== 'ai' || current.eliminated) return;

    const hadCombat = events.some((e) => e.kind === 'combatResolved');
    const delay = hadCombat ? TICK_DELAY_AFTER_COMBAT_MS : TICK_DELAY_MS;
    this.pendingTick = this.scene.time.delayedCall(delay, () => {
      this.pendingTick = undefined;
      this.tick();
    });
  }

  private tick(): void {
    const state = this.store.getState();
    if (state.status.kind !== 'inProgress') return;
    const current = state.players.find(
      (p) => p.id === state.turn.currentPlayer
    );
    if (!current || current.kind !== 'ai' || current.eliminated) return;

    const action = chooseAction(state, this.store.getMap());
    if (!action) return;

    const result = this.store.dispatch(action);
    if (!result.ok) {
      // chooseAction is supposed to only emit valid actions. If we ever
      // produce one the validator rejects, log loudly and bail to a
      // safe state by ending the turn so play doesn't deadlock.
      console.error(
        'AiDriver: validator rejected AI action',
        action,
        result.reason
      );
      this.store.dispatch({
        type: 'endTurn',
        playerId: state.turn.currentPlayer,
      });
    }
  }
}
